import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getDecodedToken, removeToken, hasPermission, savePermissions, canOpenPage } from "../helpers/auth";
import { fetchById } from "../helpers/actions";
import { getMyPermissionsUrl, getLeadsUrl, getTherapists, adminTodoUrl } from "../helpers/urls";
import CommandPalette from "./command-palette";
import { useLastMinuteAlerts, desktopAlertsPermission, requestDesktopAlerts } from "../helpers/useLastMinuteAlerts";
import { useStaffLive } from "../helpers/useStaffLive";
import {
  SpaceDashboardRounded, PsychologyRounded, Diversity3Rounded,
  CalendarMonthRounded, StarRounded, ReceiptLongRounded, Inventory2Rounded,
  CloudUploadRounded, ArticleRounded, NotificationsNoneRounded,
  AssignmentRounded, EditNoteRounded, GroupsRounded,
  AdminPanelSettingsRounded, LockRounded, LogoutRounded,
  EditRounded, MenuRounded, MenuOpenRounded, ExpandMoreRounded,
  LibraryBooksRounded, FolderSharedRounded,
  ChatBubbleOutlineRounded, MarkEmailReadRounded, WorkspacePremiumRounded,
  MailRounded, VolunteerActivismRounded, SchoolRounded, SummarizeRounded,
  MeetingRoomRounded, SelfImprovementRounded, HistoryRounded,
  EventAvailableRounded, RequestQuoteRounded, ForwardToInboxRounded, DescriptionRounded, BadgeRounded, HandshakeRounded, CardMembershipRounded, ForumRounded, PriceChangeRounded, LocalOfferRounded, CampaignRounded, PaymentsRounded, ApartmentRounded, FolderCopyRounded, AssignmentIndRounded,
  TaskAltRounded, SettingsRounded, SellRounded, SearchRounded, AddRounded, StarBorderRounded, PushPinRounded, CloseRounded,
} from "@mui/icons-material";
import BrandLogo from "./brand-logo";
import AdminBell from "./admin-bell";

/* Navigation in hubs. Every page keeps its own path (Roles gives access per path — "page:<path>"), so a hub
   is a group of pages: the sidebar folds them under one title and a page shows its hub's pages as tabs on
   top (HubTabs below). Titled groups fold open/closed in the expanded sidebar (only the group of the current
   page starts open); the icon rail still shows every item. `more: true` groups sit under a "More" label. */
export const NAV_SECTIONS = [
  { title: null, items: [
    { path: "/home", icon: <SpaceDashboardRounded />, label: "Home", alwaysVisible: true },
    { path: "/todo", icon: <TaskAltRounded />, label: "To-do", alwaysVisible: true },
  ]},
  { title: "Clients", icon: <Diversity3Rounded />, items: [
    { path: "/clients",     icon: <Diversity3Rounded />,       label: "Clients",     permKey: "clients" },
    { path: "/client-360", icon: <AssignmentIndRounded />, label: "Client 360", permKey: "clients" },
    { path: "/appointments", icon: <CalendarMonthRounded />,   label: "Schedules",  permKey: "bookings" },
    { path: "/session-packs", icon: <Inventory2Rounded />,     label: "Session Packs", permKey: "bookings" },
    { path: "/leads",       icon: <AssignmentRounded />,       label: "Leads",       permKey: "leads" },
    { path: "/intake",      icon: <FolderSharedRounded />,     label: "Intake",      permKey: "clients" },
    { path: "/chat-users",  icon: <ChatBubbleOutlineRounded />, label: "Chats", permKey: "clients" },
  ]},
  { title: "Therapists", icon: <PsychologyRounded />, items: [
    { path: "/therapists",  icon: <PsychologyRounded />,       label: "Therapists",  permKey: "therapists" },
    { path: "/subscriptions", icon: <CardMembershipRounded />, label: "Subscriptions", permKey: "therapists" },
    { path: "/therapist-comms", icon: <ForumRounded />,       label: "Therapist Comms", permKey: "therapists" },
    { path: "/therapist-payouts", icon: <PaymentsRounded />,  label: "Payouts",         superAdminOnly: true },
  ]},
  { title: "Noida Centre", icon: <ApartmentRounded />, items: [
    { path: "/noida-appointments", icon: <EventAvailableRounded />, label: "CYT Noida", permKey: "noidaCenter" },
    { path: "/reception",   icon: <MeetingRoomRounded />,      label: "Reception",   permKey: "clients" },
    { path: "/therapy-room", icon: <SelfImprovementRounded />, label: "Therapy Room", superAdminOnly: true },
    { path: "/collaborations", icon: <HandshakeRounded />, label: "Collaborations", permKey: "noidaCenter" },
  ]},
  { title: "Content", icon: <CampaignRounded />, items: [
    { path: "/manage-blogs", icon: <ArticleRounded />,           label: "Blogs",        permKey: "blogs" },
    { path: "/blogs",        icon: <EditNoteRounded />,          label: "Write a blog", permKey: "blogs" },
    { path: "/reviews",      icon: <StarRounded />,              label: "Reviews",      permKey: "reviews" },
    { path: "/resources",    icon: <LibraryBooksRounded />,      label: "Resources",    permKey: "resources" },
    { path: "/newsletter",   icon: <MailRounded />,              label: "Newsletter",   permKey: "leads" },
    { path: "/notifications", icon: <NotificationsNoneRounded />, label: "Notifications", permKey: "notifications" },
    { path: "/email-logs",    icon: <MarkEmailReadRounded />,     label: "Sent Emails",   permKey: "clients" },
    { path: "/outreach-email", icon: <ForwardToInboxRounded />,   label: "Outreach Email", permKey: "outreach" },
  ]},
  { title: "Documents", icon: <FolderCopyRounded />, more: true, items: [
    { path: "/invoices",     icon: <ReceiptLongRounded />,     label: "Invoices",   permKey: "invoices" },
    { path: "/quotations",   icon: <RequestQuoteRounded />,    label: "Quotations", permKey: "quotations" },
    { path: "/offer-letters", icon: <ForwardToInboxRounded />,  label: "Offer Letters", permKey: "offerLetters" },
    { path: "/letterheads",  icon: <DescriptionRounded />,     label: "Letterheads",   permKey: "letterheads" },
    { path: "/certificates", icon: <WorkspacePremiumRounded />,  label: "Certificates", permKey: "certificates" },
    { path: "/reports", icon: <SummarizeRounded />, label: "Session Reports", permKey: "reports" },
  ]},
  { title: "Interns", icon: <SchoolRounded />, more: true, items: [
    { path: "/probono",      icon: <VolunteerActivismRounded />, label: "Probono Interns", permKey: "probono" },
    { path: "/internship-applications", icon: <SchoolRounded />, label: "Internships", permKey: "probono" },
    { path: "/trainee-portal", icon: <AssignmentIndRounded />, label: "Trainee Portal", permKey: "probono" },
  ]},
  // every price and rule in one place (the super admin's own pages are added under it — ADMIN_SECTION)
  { title: "Settings", icon: <SettingsRounded />, items: [
    { path: "/assessment-pricing", icon: <PriceChangeRounded />, label: "Assessment Pricing", permKey: "therapists" },
    { path: "/pack-pricing", icon: <SellRounded />, label: "Pack Pricing", permKey: "bookings" },
    { path: "/coupons-upgrade", icon: <LocalOfferRounded />, label: "Coupons Upgrade", permKey: "therapists" },
    { path: "/team", icon: <BadgeRounded />, label: "Staff & My Day", permKey: "staff" },
  ]},
];

/* super-admin-only — shown inside the Settings hub */
export const ADMIN_SECTION = { title: "Admin", icon: <AdminPanelSettingsRounded />, items: [
  { path: "/admins",         icon: <GroupsRounded />,             label: "Team members" },
  { path: "/roles",          icon: <AdminPanelSettingsRounded />, label: "Roles" },
  { path: "/lead-activity",  icon: <HistoryRounded />,            label: "Lead Activity" },
  { path: "/import-clients", icon: <CloudUploadRounded />,        label: "Import Clients" },
]};

const ALL_ITEMS = [...NAV_SECTIONS.flatMap(s => s.items), ...ADMIN_SECTION.items, { path: "/edit-profile", label: "Edit Profile" }];
const TITLE_BY_PATH = Object.fromEntries(ALL_ITEMS.map(i => [i.path, i.label]));
const BY_LENGTH = [...ALL_ITEMS].sort((a, b) => b.path.length - a.path.length);

// can the signed-in admin open this page? (the same rule as the sidebar — used by the To-do list)
export function canOpenNavPath(path) {
  const p = String(path || "").split("?")[0];
  const item = BY_LENGTH.find((i) => p === i.path || p.startsWith(`${i.path}/`));
  const d = getDecodedToken();
  const sup = !!d && d.role === 2 && !d.roleId;
  if (!item || item.alwaysVisible) return true;
  if (item.superAdminOnly || ADMIN_SECTION.items.includes(item)) return sup;
  return sup || canOpenPage(item.path, item.permKey);
}

// "+" in the top bar and the search box: things admins start often (shown only if the page can be opened)
const QUICK_ACTIONS = [
  { label: "Add a client", to: "/clientregistration", permKey: "clients" },
  { label: "Add a therapist", to: "/therapistregistration", permKey: "therapists" },
  { label: "Send a notification", to: "/notifications", permKey: "notifications" },
  { label: "Create an invoice", to: "/invoices", permKey: "invoices" },
  { label: "Create a quotation", to: "/quotations", permKey: "quotations" },
  { label: "Write a blog", to: "/blogs", permKey: "blogs" },
  { label: "Send an outreach email", to: "/outreach-email", permKey: "outreach" },
];
const PIN_KEY = "cyt_nav_pins";
const readPins = () => { try { const v = JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); return Array.isArray(v) ? v.slice(0, 8) : []; } catch { return []; } };

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.2].forEach((t, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = i === 0 ? 880 : 1100;
      g.gain.setValueAtTime(0.22, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.3);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.35);
    });
  } catch {}
}

/* Phone-style "ring ring" — three short double rings (~2.4 s). Browsers only allow sound after the admin
   has clicked somewhere on the page once, which always happens in normal use. */
function playRing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tone = (start, f1, f2) => {
      [f1, f2].forEach((f) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + start + 0.02);
        g.gain.setValueAtTime(0.16, ctx.currentTime + start + 0.34);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.4);
        o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + 0.42);
      });
    };
    [0, 0.8, 1.6].forEach((t) => { tone(t, 440, 480); tone(t + 0.45, 440, 480); });
    setTimeout(() => ctx.close().catch(() => {}), 3000);
  } catch {}
}

/* Full-width bar that slides down from the top when a new therapist signs up. */
function TherapistBar({ alert, onClose, onView }) {
  const multi = alert.count > 1;
  const first = alert.names?.[0] || "A new therapist";
  const initials = first.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div role="alert" className="tb-bar">
      <div className="tb-inner">
        <span className="tb-ring" aria-hidden="true">🔔</span>
        <span className="tb-av">{multi ? `+${alert.count}` : initials}</span>
        <div className="tb-text">
          <div className="tb-title">{alert.title || (multi ? `${alert.count} new therapists registered` : "New therapist registered")}</div>
          <div className="tb-sub">
            {multi ? alert.names.slice(0, 3).join(", ") + (alert.names.length > 3 ? ` +${alert.names.length - 3} more` : "")
              : [first, alert.type, alert.state].filter(Boolean).join(" · ")}
            <span className="tb-when"> · just now · {alert.whenText || "waiting for your review"}</span>
          </div>
        </div>
        <button type="button" className="tb-view" onClick={onView}>Review now</button>
        <button type="button" className="tb-x" onClick={onClose} aria-label="Dismiss">✕</button>
      </div>
      <div className="tb-timer" />
    </div>
  );
}

function GlobalAlert({ alert, type, top, onClose, onView }) {
  const isLead = type === "lead";
  const grad = isLead
    ? "linear-gradient(135deg,#92400e,#b45309,#d97706)"
    : "linear-gradient(135deg,#14532d,#14532d,#186640)";
  const icon = isLead ? "🔔" : "👤";
  const title = isLead
    ? (alert.count > 1 ? `${alert.count} new leads arrived!` : "New lead arrived!")
    : (alert.count > 1 ? `${alert.count} new therapists joined!` : "New therapist registered!");
  return (
    <div style={{ position:"fixed", top, left:"50%", transform:"translateX(-50%)", zIndex:99998, width:"calc(100% - 32px)", maxWidth:500, animation:"glSlide .35s cubic-bezier(.34,1.56,.64,1)" }}>
      <div style={{ background:grad, borderRadius:16, padding:"13px 16px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 8px 32px rgba(0,0,0,.45),0 0 0 1px rgba(255,255,255,.1)", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 20% 50%,rgba(255,255,255,.1),transparent 60%)", pointerEvents:"none" }} />
        <div style={{ width:40, height:40, borderRadius:11, background:"rgba(255,255,255,.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:800, color:"#fff", lineHeight:1.2 }}>{title}</div>
          {alert.names?.length > 0 && (
            <div style={{ fontSize:11, color:"rgba(255,255,255,.72)", marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
              {alert.names.slice(0,3).join(", ")}{alert.names.length > 3 ? ` +${alert.names.length-3} more` : ""}
            </div>
          )}
        </div>
        <button onClick={onView} style={{ flexShrink:0, background:"rgba(255,255,255,.22)", border:"1.5px solid rgba(255,255,255,.3)", borderRadius:9, color:"#fff", fontSize:12, fontWeight:800, padding:"6px 14px", cursor:"pointer" }}>View</button>
        <button onClick={onClose} style={{ flexShrink:0, background:"none", border:"none", color:"rgba(255,255,255,.55)", fontSize:18, cursor:"pointer", padding:"0 4px", lineHeight:1 }}>✕</button>
      </div>
    </div>
  );
}

function useClock(everyMs = 1000) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

const pad2 = (n) => String(n).padStart(2, "0");
const CLOCK_DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const CLOCK_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* The clocks tick on their own: keeping the time in MainLayout's state re-rendered the whole
   layout every second, which re-mounted the sidebar links mid-click (clicks got lost). */
function TopClock({ firstName }) {
  const now = useClock(15000); // shows hh:mm only
  return <span className="ls-top-clock">{firstName} · {pad2(now.getHours())}:{pad2(now.getMinutes())}</span>;
}

function LockClock() {
  const now = useClock(1000);
  return (
    <>
      <div className="lock-clock">
        {pad2(now.getHours())}<span className="lock-clock-colon">:</span>{pad2(now.getMinutes())}
        <span className="lock-clock-ss">:{pad2(now.getSeconds())}</span>
      </div>
      <div className="lock-date">{`${CLOCK_DAYS[now.getDay()]} ${now.getDate()} ${CLOCK_MONTHS[now.getMonth()]}`}</div>
    </>
  );
}

/* Pages that need edge-to-edge content with no page-wrapper padding. */
const NO_PAD_ROUTES = ["/intake"];

export default function MainLayout({ children }) {
  const token        = getDecodedToken();
  const isSuperAdmin = token && !token.roleId;
  const profileNameKey = `cyt_pname_${token?.userId}`;
  const adminName    = token?.name
    || localStorage.getItem(profileNameKey)
    || token?.email?.split("@")[0]
    || "Admin";
  const adminAvatar  = localStorage.getItem("cyt_avatar") || null;
  const { pathname } = useLocation();
  const noPad = NO_PAD_ROUTES.includes(pathname);
  const navigate     = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);     // mobile / tablet overlay
  const [paletteOpen, setPaletteOpen] = useState(false);    // Ctrl+K search
  const [quickOpen, setQuickOpen] = useState(false);        // "+" menu
  const [pins, setPins] = useState(readPins);               // pages pinned to the top of the menu (this device)
  const [todoCount, setTodoCount] = useState(0);            // urgent To-do items, for the badge
  const [collapsed,  setCollapsed]  = useState(() => localStorage.getItem("cyt_side_collapsed") === "1");
  const [accountOpen, setAccountOpen] = useState(false);
  const [isLocked,       setIsLocked]       = useState(() => localStorage.getItem("cyt_locked") === "1");
  const [leadsAlert,     setLeadsAlert]     = useState(null);
  const [therapistAlert, setTherapistAlert] = useState(null);
  const prevLeadsRef      = useRef(null);
  const prevTherapistRef  = useRef(null);
  const [permsReady, setPermsReady] = useState(!token?.roleId);
  const [lockPass,   setLockPass]   = useState("");
  const [showPass,   setShowPass]   = useState(false);
  const [lockError,  setLockError]  = useState(false);
  const [shake,      setShake]      = useState(false);
  const inputRef = useRef(null);

  /* Fetch permissions for role-based admins */
  useEffect(() => {
    if (!token?.roleId) return;
    const load = async () => {
      let perms = null;
      try {
        const res = await fetchById(getMyPermissionsUrl);
        if (res.status) perms = res.permissions ?? null;
      } catch {}
      savePermissions(perms ?? []);
      setPermsReady(true);
    };
    load();
  }, []); // eslint-disable-line

  /* Global leads + therapist polling — runs on every page */
  useEffect(() => {
    const pollLeads = async () => {
      try {
        const res = await fetchById(getLeadsUrl);
        if (res?.status) {
          const fresh = res.data || [];
          if (prevLeadsRef.current !== null && fresh.length > prevLeadsRef.current) {
            const diff = fresh.length - prevLeadsRef.current;
            const names = fresh.slice(0, diff).map(l => l.name || l.fullName).filter(Boolean);
            setLeadsAlert({ count: diff, names });
            playBeep();
          }
          prevLeadsRef.current = fresh.length;
        }
      } catch {}
    };
    const pollTherapists = async () => {
      try {
        const res = await fetchById(getTherapists);
        if (res?.status) {
          const fresh = res.data || [];
          if (prevTherapistRef.current !== null && fresh.length > prevTherapistRef.current) {
            const diff = fresh.length - prevTherapistRef.current;
            const names = fresh.slice(0, diff).map(t => t.user?.name).filter(Boolean);
            setTherapistAlert({ count: diff, names, id: diff === 1 ? fresh[0]?._id : null, type: diff === 1 ? fresh[0]?.profile_type : "", state: diff === 1 ? fresh[0]?.user?.state : "", at: Date.now() });
            playRing();
          }
          prevTherapistRef.current = fresh.length;
        }
      } catch {}
    };
    pollLeads(); pollTherapists();
    const li = setInterval(pollLeads, 15000);
    const ti = setInterval(pollTherapists, 30000);
    return () => { clearInterval(li); clearInterval(ti); };
  }, []);

  /* Tab blink — leads */
  useEffect(() => {
    if (!leadsAlert) return;
    const orig = document.title; let on = true;
    const t = setInterval(() => { document.title = on ? `🔔 ${leadsAlert.count} New Lead${leadsAlert.count>1?"s":""}!` : orig; on = !on; }, 700);
    const d = setTimeout(() => setLeadsAlert(null), 10000);
    return () => { clearInterval(t); clearTimeout(d); document.title = orig; };
  }, [leadsAlert]);

  /* Instant: the backend announces a finished therapist sign-up on the live stream */
  useStaffLive((scope, data) => {
    if (scope === "therapist-resubmitted" && data && canSee("therapists")) {
      setTherapistAlert({ count: 1, names: [data.name], id: data.id, type: (data.items || []).join(", "), state: "", at: Date.now(),
        title: "Therapist completed their profile", whenText: "check it again" });
      playRing();
      window.dispatchEvent(new CustomEvent("cyt:therapist-registered", { detail: data }));
      return;
    }
    if (scope !== "therapist-registered" || !data) return;
    if (!canSee("therapists")) return;
    prevTherapistRef.current = (prevTherapistRef.current ?? 0) + 1; // the 30s check must not announce it again
    setTherapistAlert({ count: 1, names: [data.name], id: data.id, type: data.type, state: data.state, at: Date.now() });
    playRing();
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
        const n = new Notification("New therapist registered", { body: [data.name, data.type].filter(Boolean).join(" · "), tag: `therapist-${data.id}` });
        n.onclick = () => { window.focus(); navigate(`/therapists/${data.id}`); n.close(); };
      }
    } catch { /* notifications unavailable */ }
    window.dispatchEvent(new CustomEvent("cyt:therapist-registered", { detail: data }));
  });

  /* Tab blink — therapists */
  useEffect(() => {
    if (!therapistAlert) return;
    const orig = document.title; let on = true;
    const t = setInterval(() => { document.title = on ? `🟢 ${therapistAlert.count} New Therapist${therapistAlert.count>1?"s":""}!` : orig; on = !on; }, 900);
    const d = setTimeout(() => setTherapistAlert(null), 25000);
    return () => { clearInterval(t); clearTimeout(d); document.title = orig; };
  }, [therapistAlert]);

  useEffect(() => {
    if (isLocked) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isLocked]);

  /* close drawer + account menu on route change */
  useEffect(() => { setDrawerOpen(false); setAccountOpen(false); setQuickOpen(false); }, [pathname]);

  /* Ctrl+K / ⌘K opens the search anywhere */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* the To-do badge: urgent (red / amber) items this admin can open, checked every 2 minutes */
  useEffect(() => {
    const load = () => fetchById(adminTodoUrl)
      .then((r) => { if (r?.status) setTodoCount((r.data?.items || []).filter((i) => (i.tone === "red" || i.tone === "amber") && canOpenNavPath(i.to)).length); })
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  /* close the "+" menu on an outside click */
  useEffect(() => {
    if (!quickOpen) return undefined;
    const h = (e) => { if (!e.target.closest(".ls-quick")) setQuickOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [quickOpen]);

  /* close account menu on outside click */
  useEffect(() => {
    const h = (e) => { if (!e.target.closest(".ls-acct-wrap")) setAccountOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(c => { const n = !c; try { localStorage.setItem("cyt_side_collapsed", n ? "1" : "0"); } catch {} return n; });
  }, []);

  const handleLock = useCallback(() => {
    setAccountOpen(false);
    setLockPass(""); setLockError(false);
    setIsLocked(true);
    localStorage.setItem("cyt_locked", "1");
  }, []);

  const handleLogout = useCallback(() => {
    setAccountOpen(false);
    removeToken();
    navigate("/login");
  }, [navigate]);

  const handleUnlock = useCallback(() => {
    if (lockPass.toLowerCase().includes("cyt")) {
      setIsLocked(false);
      localStorage.removeItem("cyt_locked");
      setLockPass(""); setLockError(false);
    } else {
      setLockError(true); setShake(true); setLockPass("");
      setTimeout(() => setShake(false), 600);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [lockPass]);

  /* permission-filtered nav */
  const canSee = (permKey) => isSuperAdmin || (!permKey) || (permsReady && hasPermission(permKey));

  /* Last-minute booking requests from the public page: staff have ~10 min to answer,
     so this beeps / notifies / reminds on every page, not just the Noida one. */
  const lm = useLastMinuteAlerts(canSee("noidaCenter"));
  const [dtPerm, setDtPerm] = useState(() => desktopAlertsPermission());
  const canSeeItem = (i) => i.alwaysVisible || (i.superAdminOnly ? isSuperAdmin : (isSuperAdmin || (permsReady && canOpenPage(i.path, i.permKey))));
  const sections = NAV_SECTIONS
    .map(s => ({ ...s, items: [...s.items, ...(s.title === "Settings" && isSuperAdmin ? ADMIN_SECTION.items : [])].filter(canSeeItem) }))
    .filter(s => s.items.length > 0);
  const isOn = (item) => pathname === item.path || pathname.startsWith(`${item.path}/`);
  const visibleItems = sections.flatMap((x) => x.items.map((i) => ({ ...i, hub: x.title || "" })));
  const pinnedItems = pins.map((p) => visibleItems.find((i) => i.path === p)).filter(Boolean);
  const togglePin = (path) => setPins((list) => {
    const next = list.includes(path) ? list.filter((x) => x !== path) : [...list, path].slice(-8);
    try { localStorage.setItem(PIN_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  // the hub of the page on screen → its pages as tabs above the page
  const hub = sections.find((x) => x.title && x.items.some(isOn));
  const currentItem = BY_LENGTH.find((i) => visibleItems.some((v) => v.path === i.path) && isOn(i));
  const quickActions = QUICK_ACTIONS.filter((a) => isSuperAdmin || (permsReady && canOpenPage(a.to, a.permKey)));
  // phones: "More" opens a bottom sheet with every page as an icon, 12 per swipeable page
  const [sheet, setSheet] = useState(false);
  const [sheetPage, setSheetPage] = useState(0);
  useEffect(() => { setSheet(false); }, [pathname]);
  const sheetItems = sections.flatMap((x) => x.items);
  const sheetPages = [];
  for (let i = 0; i < sheetItems.length; i += 12) sheetPages.push(sheetItems.slice(i, i + 12));
  // groups opened by hand are remembered on this device; the group of the current page is always open
  const [openGroups, setOpenGroups] = useState(() => { try { return JSON.parse(localStorage.getItem("cyt_nav_open") || "[]"); } catch { return []; } });
  const toggleGroup = (title) => setOpenGroups((g) => {
    const next = g.includes(title) ? [] : [title];
    try { localStorage.setItem("cyt_nav_open", JSON.stringify(next)); } catch {}
    return next;
  });

  const firstName = adminName?.split(" ")[0];
  const pageTitle = TITLE_BY_PATH[pathname]
    || (pathname.replace(/^\//, "").split("/")[0] || "overview").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  // Expanded sidebar is as wide as its longest label (measured in the bold
  // "active" style so the selected item never clips), not a fixed 240px.
  const sideRef = useRef(null);
  const [sideOpenW, setSideOpenW] = useState(null);
  useLayoutEffect(() => {
    const side = sideRef.current;
    if (!side) return;
    const labels = Array.from(side.querySelectorAll(".ls-label"));
    if (!labels.length) return;
    const fam = window.getComputedStyle(labels[0]).fontFamily;
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.font = `700 13.5px ${fam}`;
    const widest = Math.max(...labels.map((l) => ctx.measureText(l.textContent || "").width));
    // nav padding 20 + item padding 20 + icon 20 + gap 10 + badge room 8 + scrollbar/border 10;
    // never narrower than the head needs for the full logo + collapse button (212)
    setSideOpenW(Math.min(272, Math.max(220, Math.ceil(widest + 100))));
  });

  const navItem = (item) => (
    <Link
      key={item.path}
      to={item.path}
      data-label={item.label}
      className={`ls-item ${isOn(item) ? "on" : ""}`}
      onClick={() => setDrawerOpen(false)}
    >
      <span className="ls-item-ic">{item.icon}</span>
      <span className="ls-label">{item.label}</span>
      {item.path === "/noida-appointments" && lm.pending.length > 0 && <span className="ls-lm-badge" aria-label={lm.pending.length + " last-minute requests waiting"}>{lm.pending.length}</span>}
      {item.path === "/todo" && todoCount > 0 && <span className="ls-lm-badge amber" aria-label={todoCount + " urgent to-do items"}>{todoCount}</span>}
    </Link>
  );

  const accountMenu = () => (
    <div className="ls-acct-menu">
      <div className="ls-acct-head">
        {adminAvatar
          ? <img src={adminAvatar} alt={adminName} className="ls-acct-av" />
          : <img src="/cyt-mark.svg" alt="" className="ls-brand-av lg" />}
        <div style={{ minWidth: 0 }}>
          <div className="ls-acct-name">{adminName}</div>
          <div className="ls-acct-role">{isSuperAdmin ? "Super Admin" : "Admin"}</div>
        </div>
      </div>
      <button className="ls-acct-item" onClick={() => { setAccountOpen(false); navigate("/edit-profile"); }}>
        <EditRounded /> Edit Profile
      </button>
      <button className="ls-acct-item" onClick={handleLock}>
        <LockRounded /> Lock Screen
      </button>
      <div className="ls-acct-sep" />
      <button className="ls-acct-item danger" onClick={handleLogout}>
        <LogoutRounded /> Log out
      </button>
    </div>
  );

  return (
    <div className={`main-wrapper ${collapsed ? "ls-collapsed" : ""} ${drawerOpen ? "ls-drawer-open" : ""}`} style={sideOpenW ? { "--side-open": `${sideOpenW}px` } : undefined}>
      <style>{`
        :root {
          --side-w: 240px; --side-rail: 64px; --top-h: 52px;
          --titlebar-h: 52px; --sidebar-w: 240px; /* legacy aliases */
          --ls-ink:#0b1712; --ls-ink-2:#4b525a; --ls-muted:#8a9099;
          --ls-line:#e2e8e4; --ls-bg:#ffffff; --ls-hover:#f7faf8;
          --ls-accent:#1e7a4c; --ls-accent-soft:#f1f8f4;
          /* Sidebar palette (kept as its own --ls-side-* set, separate from
             --ls-*, from an earlier dark-sidebar experiment that didn't
             land — reverted back to light here, but every .ls-side rule
             already points at these names so only the values change). */
          --ls-side-bg: #ffffff;
          --ls-side-ink: #0b1712;
          --ls-side-ink-2: #4b525a;
          --ls-side-muted: #8a9099;
          --ls-side-line: #e2e8e4;
          --ls-side-hover: #f7faf8;
          --ls-side-accent: #1e7a4c;
          --ls-side-accent-soft: #f1f8f4;
        }
        /* desktop collapse toggle only shrinks the pinned column above 1024 */
        .main-wrapper { --side-w: var(--side-open, 240px); }
        @media (min-width: 1024px) { .main-wrapper.ls-collapsed { --side-w: var(--side-rail); } }

        /* ══ SLIM TOP BAR ══════════════════════════════════════ */
        .ls-top {
          position: fixed; top: 0; right: 0; left: 0;
          height: var(--top-h);
          background: var(--ls-bg);
          border-bottom: 1px solid var(--ls-line);
          display: flex; align-items: center; gap: 12px;
          padding: 0 16px 0 14px;
          z-index: 1004;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .ls-burger {
          display: none; width: 34px; height: 34px; border: none; background: none;
          border-radius: 8px; color: var(--ls-ink-2); cursor: pointer;
          align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ls-burger:hover { background: var(--ls-hover); }
        .ls-burger svg { font-size: 21px !important; }
        .ls-page-title { font-size: 15px; font-weight: 700; color: var(--ls-ink); letter-spacing: -0.01em; }
        .ls-top-sp { margin-left: auto; }
        .ls-top-clock { font-size: 12px; color: var(--ls-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .ls-bell {
          width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
          color: var(--ls-ink-2); text-decoration: none !important; position: relative; flex-shrink: 0;
        }
        .ls-bell:hover { background: var(--ls-hover); }
        .ls-bell svg { font-size: 19px !important; }
        .ls-bell-dot { position: absolute; top: 7px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: #1e7a4c; }

        .ls-acct-wrap { position: relative; flex-shrink: 0; }
        .ls-acct-btn {
          display: flex; align-items: center; gap: 8px; border: 1px solid var(--ls-line);
          background: var(--ls-bg); border-radius: 999px; padding: 3px 10px 3px 3px; cursor: pointer;
          font-family: inherit;
        }
        .ls-acct-btn:hover { background: var(--ls-hover); }
        .ls-acct-btn .ls-acct-nm { font-size: 12.5px; font-weight: 600; color: var(--ls-ink); max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ls-acct-btn svg { font-size: 16px !important; color: var(--ls-muted); }
        .ls-avatar {
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          background: linear-gradient(135deg,#14532d,#2c8c5c); color: #fff;
          display: grid; place-items: center; font-size: 10.5px; font-weight: 800;
        }
        .ls-avatar.lg { width: 34px; height: 34px; font-size: 13px; }
        /* no profile photo → the CYT mark stands in for the avatar */
        .ls-brand-av { width: 26px; height: 26px; flex-shrink: 0; display: block; }
        .ls-brand-av.lg { width: 34px; height: 34px; }
        .ls-acct-av { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }

        .ls-acct-menu {
          position: absolute; min-width: 220px;
          background: var(--ls-bg); border: 1px solid var(--ls-line); border-radius: 12px;
          box-shadow: 0 16px 40px rgba(20,22,25,.14); padding: 6px; z-index: 1010;
          animation: lsPop .14s ease both;
        }
        /* the account menu now lives in the sidebar footer — open upward */
        .ls-side-foot .ls-acct-menu { bottom: calc(100% + 8px); left: 8px; right: 8px; }
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .main-wrapper:not(.ls-drawer-open) .ls-side-foot .ls-acct-menu { left: 8px; right: auto; }
        }
        @keyframes lsPop { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .ls-acct-head { display: flex; align-items: center; gap: 10px; padding: 9px 10px 10px; border-bottom: 1px solid var(--ls-line); margin-bottom: 4px; }
        .ls-acct-name { font-size: 13px; font-weight: 700; color: var(--ls-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ls-acct-role { font-size: 10.5px; font-weight: 600; color: var(--ls-accent); margin-top: 1px; }
        .ls-acct-item {
          display: flex; align-items: center; gap: 9px; width: 100%; text-align: left;
          padding: 8px 10px; border: none; background: none; border-radius: 7px; cursor: pointer;
          font-size: 12.5px; font-weight: 500; color: var(--ls-ink-2); font-family: inherit;
        }
        .ls-acct-item:hover { background: var(--ls-hover); }
        .ls-acct-item svg { font-size: 16px !important; color: var(--ls-muted); }
        .ls-acct-item.danger { color: #c4302b; }
        .ls-acct-item.danger svg { color: #c4302b; }
        .ls-acct-item.danger:hover { background: #fdeeed; }
        .ls-acct-sep { height: 1px; background: var(--ls-line); margin: 4px 2px; }

        /* ══ LABELLED SIDEBAR ══════════════════════════════════ */
        .ls-side {
          position: fixed; top: 0; bottom: 0; left: 0;
          width: var(--side-w);
          background: var(--ls-side-bg); border-right: 1px solid var(--ls-side-line);
          display: flex; flex-direction: column;
          z-index: 1006;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          transition: width .22s cubic-bezier(.4,0,.2,1), transform .24s cubic-bezier(.4,0,.2,1);
        }
        .ls-side-head {
          height: var(--top-h); flex-shrink: 0;
          display: flex; align-items: center; gap: 10px;
          padding: 0 10px 0 14px; border-bottom: 1px solid var(--ls-side-line);
        }
        .ls-wordmark { font-size: 13.5px; font-weight: 800; color: var(--ls-side-ink); letter-spacing: -0.02em; white-space: nowrap; flex: 1; overflow: hidden; }
        /* the square brand mark stands in for the logo wherever the rail hides the wordmark */
        .ls-mark { display: none; width: 30px; height: 30px; flex-shrink: 0; }
        @media (min-width: 1024px) { .ls-collapsed .ls-side .ls-mark { display: block; } }
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .ls-side .ls-mark { display: block; }
          .ls-drawer-open .ls-side .ls-mark { display: none; }
        }
        .ls-collapse {
          width: 28px; height: 28px; border: none; background: none; border-radius: 7px;
          color: var(--ls-side-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ls-collapse:hover { background: var(--ls-side-hover); color: var(--ls-side-ink); }
        .ls-collapse svg { font-size: 18px !important; }

        .ls-nav { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 8px 10px 12px; scrollbar-width: thin; }
        .ls-group {
          font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
          color: var(--ls-side-muted); padding: 14px 8px 5px;
        }
        .ls-group:first-child { padding-top: 6px; }
        /* foldable groups (expanded sidebar) */
        .ls-sec { margin-top: 2px; }
        .ls-group-btn { width: 100%; display: flex; align-items: center; gap: 6px; border: 0; background: none; cursor: pointer; font-family: inherit; text-align: left; border-radius: 7px; padding: 10px 8px 6px; }
        .ls-group-btn:hover { color: var(--ls-side-ink); background: var(--ls-side-hover); }
        .ls-group-btn > span { flex: 1; }
        .ls-gchev { font-size: 16px !important; transition: transform .18s; transform: rotate(-90deg); opacity: .7; }
        .ls-sec.open .ls-gchev { transform: none; }
        .ls-gcount { font-style: normal; font-size: 10px; font-weight: 800; letter-spacing: 0; background: var(--ls-side-hover); color: var(--ls-side-muted); border-radius: 999px; padding: 1px 6px; }
        .ls-sec .ls-sec-items { display: none; }
        .ls-sec.open .ls-sec-items { display: block; animation: lsOpen .16s ease-out; }
        @keyframes lsOpen { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
        .ls-item {
          position: relative;
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px; border-radius: 9px; margin-bottom: 2px;
          font-size: 13.5px; font-weight: 500; color: var(--ls-side-ink-2);
          text-decoration: none !important; white-space: nowrap;
          transition: background .12s, color .12s;
        }
        .ls-item:hover { background: var(--ls-side-hover); color: var(--ls-side-ink); text-decoration: none; }
        .ls-lm-badge { margin-left: auto; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: #dc2626; color: #fff; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; animation: lmPulse 1.4s ease-in-out infinite; }
        @keyframes lmPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,.55); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }
        .ls-collapsed .ls-lm-badge { position: absolute; top: 2px; right: 2px; margin: 0; }
        .ls-item.on { background: var(--ls-side-accent-soft); color: var(--ls-side-accent); font-weight: 700; box-shadow: inset 3px 0 0 var(--ls-side-accent); }
        .ls-item-ic { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ls-item-ic svg { font-size: 18px !important; }
        .ls-item.on .ls-item-ic svg { color: var(--ls-side-accent); }

        .ls-side-foot { flex-shrink: 0; border-top: 1px solid var(--ls-side-line); padding: 8px 10px; }
        .ls-user {
          display: flex; align-items: center; gap: 9px; width: 100%;
          padding: 7px 8px; border: none; background: none; border-radius: 8px; cursor: pointer;
          font-family: inherit; text-align: left;
        }
        .ls-user:hover { background: var(--ls-side-hover); }
        .ls-user-text { min-width: 0; flex: 1; }
        .ls-user-name { display: block; font-size: 12.5px; font-weight: 700; color: var(--ls-side-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ls-user-role { display: block; font-size: 10px; color: var(--ls-side-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ls-user > svg { font-size: 16px !important; color: var(--ls-side-muted); flex-shrink: 0; }

        /* collapsed rail (desktop-only feature) — hide text, centre icons, tooltip on hover */
        @media (min-width: 1024px) {
          .ls-collapsed .ls-side .ls-label,
          .ls-collapsed .ls-side .ls-wordmark,
          .ls-collapsed .ls-side .ls-user-text,
          .ls-collapsed .ls-side .ls-user > svg { display: none; }
          .ls-collapsed .ls-side-head { padding: 0; justify-content: center; position: relative; }
          /* the rail is too narrow for mark + button side by side: show the mark alone,
             and swap it for the expand button on hover / keyboard focus */
          .ls-collapsed .ls-side .ls-collapse {
            position: absolute; top: 0; bottom: 0; left: 0; right: 0; margin: auto;
            width: 34px; height: 34px; opacity: 0; background: var(--ls-side-hover); color: var(--ls-side-ink);
            transition: opacity .15s;
          }
          .ls-collapsed .ls-side .ls-mark { transition: opacity .15s; }
          .ls-collapsed .ls-side-head:hover .ls-collapse,
          .ls-collapsed .ls-side .ls-collapse:focus-visible { opacity: 1; }
          .ls-collapsed .ls-side-head:hover .ls-mark,
          .ls-collapsed .ls-side-head:has(.ls-collapse:focus-visible) .ls-mark { opacity: 0; }
          .ls-collapsed .ls-side .ls-nav { padding-left: 10px; padding-right: 10px; }
          .ls-collapsed .ls-side .ls-item,
          .ls-collapsed .ls-side .ls-user { justify-content: center; gap: 0; padding-left: 0; padding-right: 0; }
          .ls-collapsed .ls-side .ls-item.on { box-shadow: inset 3px 0 0 var(--ls-side-accent); }
          /* group heading → hairline separator when collapsed (no overflowing text) */
          .ls-collapsed .ls-side .ls-group {
            height: 1px; margin: 8px 6px; padding: 0;
            font-size: 0; line-height: 0; overflow: hidden;
            background: var(--ls-side-line); color: transparent;
          }
          .ls-collapsed .ls-side .ls-group:first-child { margin-top: 2px; }
          .ls-collapsed .ls-side .ls-sec .ls-sec-items { display: block; }
          .ls-collapsed .ls-side .ls-group-btn { pointer-events: none; }
          .ls-collapsed .ls-side .ls-group-btn > * { display: none !important; }
          .ls-collapsed .ls-side .ls-item:hover::after {
            content: attr(data-label);
            position: absolute; left: calc(100% + 10px); top: 50%; transform: translateY(-50%);
            background: #1f2328; color: #fff; font-size: 12px; font-weight: 600; white-space: nowrap;
            padding: 5px 9px; border-radius: 6px; z-index: 1010; box-shadow: 0 6px 20px rgba(0,0,0,.25);
            pointer-events: none;
          }
        }

        /* ══ PAGE WRAPPER ══════════════════════════════════════ */
        @keyframes cytFade { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        .page-wrapper {
          padding: calc(var(--top-h) + 20px) 28px 28px;
          background: var(--cyt-bg-page, #f6f8f6); min-height: 100vh;
          animation: cytFade 0.18s ease both;
          margin-left: 0 !important;
        }
        @media (min-width: 1024px) {
          .page-wrapper {
            padding-left: calc(var(--side-w) + 28px) !important;
            transition: padding-left 0.24s cubic-bezier(0.4,0,0.2,1);
          }
          .ls-top { left: var(--side-w); transition: left 0.24s cubic-bezier(0.4,0,0.2,1); }
          .ls-burger { display: none !important; }
        }

        /* ══ TABLET / iPad (768–1023) — pinned icon rail, hamburger opens full overlay ══ */
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .ls-side { width: var(--side-rail); }
          .ls-side .ls-label, .ls-side .ls-wordmark,
          .ls-side .ls-user-text, .ls-side .ls-user > svg, .ls-side .ls-collapse { display: none; }
          .ls-side .ls-group {
            height: 1px; margin: 8px 6px; padding: 0;
            font-size: 0; line-height: 0; overflow: hidden;
            background: var(--ls-side-line); color: transparent;
          }
          .ls-side .ls-group:first-child { margin-top: 2px; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-sec .ls-sec-items { display: block; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-group-btn { pointer-events: none; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-group-btn > * { display: none !important; }
          .ls-side-head { padding: 0; justify-content: center; }
          .ls-side .ls-nav { padding-left: 10px; padding-right: 10px; }
          .ls-side .ls-item, .ls-side .ls-user { justify-content: center; gap: 0; padding-left: 0; padding-right: 0; }
          .ls-side .ls-item:hover::after {
            content: attr(data-label);
            position: absolute; left: calc(100% + 10px); top: 50%; transform: translateY(-50%);
            background: #1f2328; color: #fff; font-size: 12px; font-weight: 600; white-space: nowrap;
            padding: 5px 9px; border-radius: 6px; z-index: 1010; box-shadow: 0 6px 20px rgba(0,0,0,.25); pointer-events: none;
          }
          .page-wrapper { padding-left: calc(var(--side-rail) + 22px) !important; }
          .ls-top { left: var(--side-rail); }
          .ls-burger { display: flex; }

          /* drawer-open → expand to full labelled sidebar as an overlay */
          .ls-drawer-open .ls-side { width: var(--side-w); box-shadow: 0 0 60px rgba(20,22,25,.25); }
          .ls-drawer-open .ls-side .ls-label,
          .ls-drawer-open .ls-side .ls-wordmark, .ls-drawer-open .ls-side .ls-user-text,
          .ls-drawer-open .ls-side .ls-user > svg { display: block; }
          .ls-drawer-open .ls-side .ls-group {
            height: auto; margin: 0; padding: 14px 8px 5px;
            font-size: 10px; line-height: 1.4; overflow: visible;
            background: none; color: var(--ls-muted);
          }
          .ls-drawer-open .ls-side .ls-nav { padding-left: 10px; padding-right: 10px; }
          .ls-drawer-open .ls-side .ls-item, .ls-drawer-open .ls-side .ls-user { justify-content: flex-start; gap: 10px; padding-left: 9px; padding-right: 9px; }
          .ls-drawer-open .ls-side-head { padding: 0 10px 0 14px; justify-content: flex-start; }
          .ls-drawer-open .ls-side .ls-item:hover::after { display: none; }
        }

        /* ══ MOBILE (<768) — off-canvas drawer ══ */
        @media (max-width: 767px), (max-width: 1023px) and (orientation: portrait) {
          .ls-side { transform: translateX(-100%); width: 264px; box-shadow: 0 0 60px rgba(20,22,25,.3); }
          .ls-side .ls-collapse { display: none; }
          .ls-drawer-open .ls-side { transform: translateX(0); }
          .page-wrapper { padding: calc(var(--top-h) + 12px) 12px 84px; overflow-x: hidden; }
          .ls-top { left: 0; }
          .ls-burger { display: flex; }
          .ls-top-clock { display: none; }
          .ls-acct-btn .ls-acct-nm { display: none; }
          .ls-acct-btn { padding: 3px; }
          .ls-acct-btn > svg { display: none; }
          input,select,textarea,.form-control { font-size: 16px !important; }
          .mob-nav { display: flex; }
        }
        @media (min-width: 1024px), (min-width: 768px) and (orientation: landscape) { .mob-nav { display: none; } }

        .ls-scrim {
          position: fixed; inset: 0; background: rgba(20,22,25,.4);
          z-index: 1005; backdrop-filter: blur(2px);
          animation: cytFade .16s ease both;
        }
        @media (min-width: 1024px) { .ls-scrim { display: none; } }

        /* ══ Brand theme for every admin page (like the trainee portal): sage page, green titles,
              white cards with soft borders, stat tiles with a green / gold / teal / amber top strip ══ */
        .page-wrapper { --cyt-bg-page: #eef1ec; }
        .page-wrapper h1 { color: #14532d; }
        .page-wrapper :is(.sd-card-t, .cc-card-t, .tc-card-h h2, .tpa-card h2, .c3-card h2) { color: #14532d; }
        .page-wrapper :is(.sd-card, .cc-card, .tc-card, .qt-card, .ol-card, .be-card, .oe-card, .nk-card, .cyt-card, .pb-card, .lh-card, .ap-card, .rp-card, .py-card, .tpa-card, .c3-card) {
          background: #fff; border-color: #e2e8e4; box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 10px 24px -22px rgba(20,83,45,.35);
        }
        .page-wrapper :is(.ap-stat, .nk-stat, .cc-kpi, .cb-stat, .sd-stat, .sb-stat, .td-stat, .td-kpi, .kpi-tile, .tr-stat, .tpa-kpi, .pb-tile) {
          border-top: 3px solid var(--tone, #1f6b47) !important;
        }
        .page-wrapper :is(.ap-stat, .nk-stat, .cc-kpi, .cb-stat, .sd-stat, .sb-stat, .td-stat, .td-kpi, .kpi-tile, .tr-stat, .tpa-kpi, .pb-tile):nth-child(4n+1) { --tone: #1f6b47; }
        .page-wrapper :is(.ap-stat, .nk-stat, .cc-kpi, .cb-stat, .sd-stat, .sb-stat, .td-stat, .td-kpi, .kpi-tile, .tr-stat, .tpa-kpi, .pb-tile):nth-child(4n+2) { --tone: #c9962c; }
        .page-wrapper :is(.ap-stat, .nk-stat, .cc-kpi, .cb-stat, .sd-stat, .sb-stat, .td-stat, .td-kpi, .kpi-tile, .tr-stat, .tpa-kpi, .pb-tile):nth-child(4n+3) { --tone: #2f7d6d; }
        .page-wrapper :is(.ap-stat, .nk-stat, .cc-kpi, .cb-stat, .sd-stat, .sb-stat, .td-stat, .td-kpi, .kpi-tile, .tr-stat, .tpa-kpi, .pb-tile):nth-child(4n+4) { --tone: #a5741f; }

        /* ══ Phones & iPads: nothing wider than the screen ══ */
        @media (max-width: 1023px) {
          .page-wrapper table { display: block; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .page-wrapper :is(input:not([type=checkbox]):not([type=radio]), select, textarea) { max-width: 100%; box-sizing: border-box; }
          .page-wrapper :is(img, video, iframe, canvas) { max-width: 100%; }
          .page-wrapper :is(td, p, li, dd) { overflow-wrap: anywhere; }
        }
        @media (max-width: 560px) {
          .page-wrapper [style*="grid-template-columns: 1fr 1fr 1fr"], .page-wrapper [style*="grid-template-columns:1fr 1fr 1fr"],
          .page-wrapper [style*="repeat(3, 1fr)"], .page-wrapper [style*="repeat(3,1fr)"],
          .page-wrapper [style*="repeat(5, 1fr)"], .page-wrapper [style*="repeat(4, 1fr)"], .page-wrapper [style*="repeat(4,1fr)"] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .page-wrapper [style*="2fr 1fr"], .page-wrapper [style*="1fr 2fr"], .page-wrapper [style*="1fr 1.5fr"],
          .page-wrapper [style*="1.3fr 0.8fr"], .page-wrapper [style*="0.6fr 0.8fr"], .page-wrapper [style*="2fr 1fr 1fr"] {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          .page-wrapper [style*="display: flex"][style*="gap"]:not([style*="flex-wrap"]), .page-wrapper [style*="display:flex"][style*="gap"]:not([style*="flex-wrap"]) { flex-wrap: wrap; }
        }

        /* ══ MOBILE "More" sheet ══ */
        .mob-sheet-bg { position: fixed; inset: 0; z-index: 1300; background: rgba(15,23,20,.4); display: flex; align-items: flex-end; animation: cytFade .16s ease both; }
        .mob-sheet { width: 100%; background: #fff; border-radius: 22px 22px 0 0; padding: 8px 0 calc(14px + env(safe-area-inset-bottom, 0px)); box-shadow: 0 -20px 40px rgba(0,0,0,.18); animation: mobUp .24s cubic-bezier(.2,.8,.2,1); }
        @keyframes mobUp { from { transform: translateY(100%); } to { transform: none; } }
        .mob-grab { width: 40px; height: 4px; border-radius: 4px; background: #d6ded9; margin: 2px auto 6px; }
        .mob-sheet-h { display: flex; align-items: center; justify-content: space-between; padding: 0 16px 8px; }
        .mob-sheet-h b { font-size: 16px; font-weight: 800; color: #0f172a; }
        .mob-sheet-h button { width: 32px; height: 32px; border-radius: 10px; border: 1px solid #e2e8e4; background: #fff; font-size: 20px; line-height: 1; color: #475569; }
        .mob-pages { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .mob-pages::-webkit-scrollbar { display: none; }
        .mob-page { flex: 0 0 100%; scroll-snap-align: start; display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px 6px; padding: 4px 12px; box-sizing: border-box; align-content: start; }
        .mob-tile { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 6px 2px; text-decoration: none !important; border-radius: 14px; }
        .mob-tile-ic { width: 50px; height: 50px; border-radius: 16px; background: #f1f7f3; color: #166534; display: grid; place-items: center; }
        .mob-tile-ic svg { font-size: 24px !important; }
        .mob-tile-l { font-size: 11px; font-weight: 700; color: #334155; text-align: center; line-height: 1.2; max-width: 78px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .mob-tile.on .mob-tile-ic { background: #166534; color: #fff; box-shadow: 0 6px 14px -6px rgba(22,101,52,.6); }
        .mob-tile.on .mob-tile-l { color: #166534; }
        .mob-dots { display: flex; justify-content: center; gap: 6px; margin-top: 10px; }
        .mob-dots i { width: 7px; height: 7px; border-radius: 50%; background: #d6ded9; transition: width .2s; }
        .mob-dots i.on { width: 18px; border-radius: 4px; background: #166534; }
        .mob-swipe { display: block; text-align: center; font-size: 11px; color: #94a3b8; margin-top: 6px; }
        @media (min-width: 1024px), (min-width: 768px) and (orientation: landscape) { .mob-sheet-bg { display: none; } }
        @media (min-width: 1024px), (min-width: 768px) and (orientation: landscape) { .mob-nav.mob-nav { display: none; } }

        /* ══ MOBILE BOTTOM NAV (kept) ══ */
        .mob-nav {
          display: flex; position: fixed; bottom: 0; left: 0; right: 0; height: 62px;
          background: var(--ls-bg); border-top: 1px solid var(--ls-line);
          align-items: center; justify-content: space-around; z-index: 1002;
          padding: 0 4px; box-shadow: 0 -4px 24px rgba(20,22,25,.08);
        }
        .mob-nav-item {
          flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 3px; padding: 7px 2px; background: none; border: none; cursor: pointer;
          color: var(--ls-muted); font-size: 10px; font-weight: 600; text-decoration: none;
          border-radius: 10px; max-width: 80px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .mob-nav-item.active { color: var(--ls-accent); }
        .mob-nav-item svg { font-size: 22px !important; }
        .mob-nav-item span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 70px; }

        /* ══ LOCK SCREEN (unchanged) ══════════════════════════ */
        .lock-screen { position:fixed; inset:0; z-index:99999; display:flex; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; }
        .lock-left { width:36%; background:linear-gradient(145deg,#0a2e17 0%,#1e7a4c 50%,#0d3d20 100%); display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:40px 36px; position:relative; overflow:hidden; }
        .lock-watermark { position:absolute; font-size:200px; font-weight:900; color:rgba(255,255,255,0.03); top:50%; left:50%; transform:translate(-50%,-50%); user-select:none; pointer-events:none; line-height:1; }
        .lock-clock { font-size:clamp(56px,7vw,80px); font-weight:100; color:rgba(255,255,255,0.95); letter-spacing:-3px; line-height:1; position:relative; z-index:1; }
        .lock-clock-colon { animation:colonBlink 1s step-start infinite; }
        @keyframes colonBlink { 50%{opacity:0.15} }
        .lock-clock-ss { font-size:0.32em; color:rgba(255,255,255,0.35); vertical-align:middle; }
        .lock-date { font-size:11px; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.3); font-weight:500; position:relative; z-index:1; margin-top:6px; }
        .lock-rule { width:40px; height:1px; background:rgba(255,255,255,0.2); margin:24px auto; position:relative; z-index:1; }
        .lock-brand { position:relative; z-index:1; }
        .lock-brand-name { font-size:26px; font-weight:900; color:#fff; line-height:1.2; }
        .lock-tagline { font-size:12px; color:rgba(255,255,255,0.42); line-height:1.65; font-style:italic; max-width:220px; margin-top:8px; }
        .lock-admin-badge { position:absolute; bottom:20px; left:50%; transform:translateX(-50%); font-size:9px; letter-spacing:3px; text-transform:uppercase; color:rgba(255,255,255,0.2); font-weight:600; white-space:nowrap; z-index:1; }
        .lock-right { flex:1; background:#f8fafc; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 40px; }
        .lock-unlock-box { width:100%; max-width:360px; display:flex; flex-direction:column; gap:18px; }
        .lock-unlock-heading { text-align:center; }
        .lock-lock-icon { width:52px; height:52px; background:#fff; border:2px solid #e2e8f0; border-radius:16px; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; color:#1e7a4c; font-size:22px; }
        .lock-unlock-title { font-size:20px; font-weight:700; color:#0f172a; }
        .lock-unlock-sub { font-size:13px; color:#94a3b8; margin-top:4px; }
        .lock-input-wrap { position:relative; }
        .lock-input { width:100%; height:48px; background:#fff; border:1.5px solid #e2e8f0; border-radius:12px; color:#0f172a; font-size:14px; letter-spacing:2px; padding:0 46px 0 16px; outline:none; transition:border-color 0.2s,box-shadow 0.2s; }
        .lock-input::placeholder { letter-spacing:0.3px; color:#cbd5e1; font-size:13px; }
        .lock-input:focus { border-color:#1e7a4c; box-shadow:0 0 0 3px rgba(30,122,76,0.1); }
        .lock-input.error { border-color:#ef4444; }
        .lock-input.shake { animation:lockShake 0.5s ease; }
        @keyframes lockShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
        .lock-eye { position:absolute; right:14px; top:50%; transform:translateY(-50%); background:none; border:none; color:#94a3b8; cursor:pointer; font-size:15px; padding:0; }
        .lock-btn { width:100%; height:48px; background:#1e7a4c; border:none; border-radius:12px; color:#fff; font-size:14px; font-weight:700; cursor:pointer; transition:background 0.18s; }
        .lock-btn:hover { background:#186640; }
        .lock-error-msg { font-size:12px; color:#ef4444; font-weight:500; }
        .lock-hint { text-align:center; font-size:11px; color:#cbd5e1; }
        @media(max-width:767px){ .lock-left{display:none;} .lock-right{background:linear-gradient(160deg,#0c3520 0%,#1e7a4c 55%,#0f4526 100%);} .lock-input{background:rgba(255,255,255,0.95);} .lock-btn{background:#fff;color:#1e7a4c;} .lock-unlock-title{color:#fff;} .lock-unlock-sub{color:rgba(255,255,255,0.55);} }

        @keyframes tbDown { from { transform: translateY(-110%); } to { transform: translateY(0); } }
        @keyframes tbShake { 0%,100% { transform: rotate(0); } 15% { transform: rotate(-18deg); } 30% { transform: rotate(16deg); } 45% { transform: rotate(-12deg); } 60% { transform: rotate(8deg); } 75% { transform: rotate(-4deg); } }
        @keyframes tbTimer { from { transform: scaleX(1); } to { transform: scaleX(0); } }
        .tb-bar { position: fixed; top: 0; left: 0; right: 0; z-index: 100000; background: linear-gradient(135deg,#0f3d22,#14532d 55%,#186640); color: #fff; box-shadow: 0 10px 30px rgba(0,0,0,.35); animation: tbDown .28s cubic-bezier(.2,.9,.3,1.2); font-family: Inter, -apple-system, "Segoe UI", sans-serif; }
        .tb-inner { max-width: 1280px; margin: 0 auto; display: flex; align-items: center; gap: 12px; padding: 12px 18px; }
        .tb-ring { font-size: 22px; display: inline-block; animation: tbShake .9s ease-in-out 3; transform-origin: 50% 10%; }
        .tb-av { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.18); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; flex-shrink: 0; }
        .tb-text { flex: 1; min-width: 0; }
        .tb-title { font-size: 15px; font-weight: 800; line-height: 1.2; }
        .tb-sub { font-size: 12.5px; color: rgba(255,255,255,.85); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tb-when { color: rgba(255,255,255,.6); }
        .tb-view { flex-shrink: 0; background: #fff; color: #14532d; border: none; border-radius: 10px; padding: 9px 16px; font-size: 13px; font-weight: 800; cursor: pointer; }
        .tb-view:hover { background: #ecfdf5; }
        .tb-x { flex-shrink: 0; background: none; border: none; color: rgba(255,255,255,.7); font-size: 18px; cursor: pointer; padding: 4px 6px; }
        .tb-timer { height: 3px; background: rgba(255,255,255,.55); transform-origin: left; animation: tbTimer 25s linear forwards; }
        @media (max-width: 640px) { .tb-inner { flex-wrap: wrap; padding: 10px 12px; gap: 10px; } .tb-text { flex-basis: calc(100% - 110px); } .tb-view { flex: 1; } .tb-sub { white-space: normal; } }
        @keyframes glSlide { from{transform:translateX(-50%) translateY(-24px);opacity:0} to{transform:translateX(-50%) translateY(0);opacity:1} }

        /* ══ BRAND THEME — same look as the therapist / client portals ══ */
        .main-wrapper { --top-h: 58px; --titlebar-h: 58px; }
        .ls-top, .ls-side, .mob-nav, .mob-nav-item { font-family: Inter, -apple-system, "Segoe UI", sans-serif; }
        .ls-top { background: linear-gradient(90deg, #14532d 0%, #166534 100%); border-bottom: 0; box-shadow: 0 6px 18px -10px rgba(4,30,16,.55); }
        .ls-burger { color: #fff; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.22); width: 38px; height: 38px; border-radius: 11px; }
        .ls-burger:hover { background: rgba(255,255,255,.2); }
        .ls-page-title { color: #fff; font-size: 16px; font-weight: 800; }
        .ls-top-clock { color: rgba(255,255,255,.75); }
        .ls-bell { color: rgba(255,255,255,.9); width: 38px; height: 38px; border-radius: 11px; }
        .ls-bell:hover { background: rgba(255,255,255,.14); }
        .ls-bell-dot { background: #f5c542; box-shadow: 0 0 0 2px #166534; }
        .ls-top-me { display: flex; align-items: center; gap: 9px; padding: 3px 12px 3px 3px; border-radius: 12px; color: #fff !important; text-decoration: none !important; min-width: 0; }
        .ls-top-me:hover { background: rgba(255,255,255,.1); }
        .ls-top-me-av { width: 34px; height: 34px; border-radius: 10px; background: #fff; color: #166534; display: grid; place-items: center; font-weight: 800; font-size: 13px; overflow: hidden; flex-shrink: 0; box-shadow: 0 0 0 2px rgba(255,255,255,.85); }
        .ls-top-me-av img { width: 100%; height: 100%; object-fit: cover; }
        .ls-top-me-txt { display: flex; flex-direction: column; line-height: 1.15; min-width: 0; }
        .ls-top-me-txt b { font-size: 13.5px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
        .ls-top-me-txt span { font-size: 11px; color: #bbf7d0; }

        /* sidebar: green head that continues the top bar, white list below */
        .ls-side-head { background: linear-gradient(90deg, #0b3320 0%, #14532d 100%); border-bottom: 0; }
        .ls-wordmark { background: #fff; border-radius: 10px; padding: 5px 9px; flex: 0 1 auto; margin-right: auto; line-height: 0; min-width: 0; overflow: hidden; }
        .ls-side-head .cyt-brand { gap: 6px !important; }
        .ls-side-head .cyt-brand > img { width: 24px; height: 24px; }
        .ls-side-head .cyt-brand-small { font-size: 7.5px !important; }
        .ls-side-head .cyt-brand-big { font-size: 15px !important; }
        .ls-mark { background: #fff; border-radius: 9px; padding: 3px; width: 34px; height: 34px; }
        .ls-collapse { color: #fff; background: rgba(255,255,255,.12); border-radius: 9px; width: 32px; height: 32px; }
        .ls-collapse:hover { background: rgba(255,255,255,.22); color: #fff; }
        .ls-nav { padding: 10px 12px 14px; }
        .ls-group { color: #94a3b8; font-weight: 800; letter-spacing: .07em; }
        .ls-item { font-size: 13.5px; font-weight: 600; color: #475569; padding: 10px 11px; border-radius: 11px; margin-bottom: 3px; }
        .ls-item-ic svg { color: #64748b; }
        .ls-item:hover { background: #e7f6ec; color: #166534; }
        .ls-item:hover .ls-item-ic svg { color: #166534; }
        .ls-item.on { background: linear-gradient(135deg, #14532d, #16a34a); color: #fff; font-weight: 700; box-shadow: 0 6px 14px -6px rgba(22,101,52,.6); }
        .ls-item.on::before { content: ""; position: absolute; left: -12px; top: 9px; bottom: 9px; width: 3px; border-radius: 0 3px 3px 0; background: #c9962c; }
        .ls-item.on .ls-item-ic svg { color: #fff; }
        .ls-collapsed .ls-side .ls-item.on, .main-wrapper .ls-side .ls-item.on { box-shadow: 0 6px 14px -6px rgba(22,101,52,.6); }
        .ls-side-foot { background: #f8faf9; }
        .ls-user:hover { background: #e7f6ec; }
        .page-wrapper { background: linear-gradient(180deg, #e9f3ec 0, #f1f6f2 320px, #f3f6f4 100%); }

        /* mobile bottom bar: active item as a solid green pill */
        .mob-nav-item.active { color: #166534; }
        .mob-nav-item.active svg { background: #166534; color: #fff; border-radius: 999px; padding: 4px 14px; width: auto !important; height: 30px; box-sizing: content-box; box-shadow: 0 4px 12px -4px rgba(22,101,52,.55); }
        @media (max-width: 767px), (max-width: 1023px) and (orientation: portrait) {
          .ls-top-me-txt { display: none; }
          .ls-top-me { padding: 3px; }
        }

        /* account menu now opens from the logo at the top of the sidebar */
        .ls-side-head { position: relative; }
        .ls-brand-btn { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; padding: 0; border: 0; background: none; cursor: pointer; text-align: left; font-family: inherit; }
        .ls-brand-chev { color: #fff; font-size: 18px !important; flex-shrink: 0; opacity: .85; }
        .ls-side-head .ls-acct-menu { top: calc(100% + 6px); bottom: auto; left: 10px; right: 10px; z-index: 1012; }
        @media (min-width: 1024px) {
          .ls-collapsed .ls-brand-chev { display: none; }
          .ls-collapsed .ls-side-head .ls-acct-menu { right: auto; width: 230px; }
          .ls-collapsed .ls-brand-btn { flex: 0 0 auto; }
        }
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .main-wrapper:not(.ls-drawer-open) .ls-brand-chev { display: none; }
          .main-wrapper:not(.ls-drawer-open) .ls-side-head .ls-acct-menu { right: auto; width: 230px; }
          .main-wrapper:not(.ls-drawer-open) .ls-brand-btn { flex: 0 0 auto; }
        }

        /* sidebar head: one white logo pill (logo + chevron) that fits, tidy collapse button */
        .ls-side-head { padding: 0 9px 0 10px; gap: 6px; }
        .ls-brand-btn { flex: 0 1 auto; margin-right: auto; height: 40px; padding: 0 6px 0 7px; gap: 2px; background: #fff; border-radius: 11px; box-shadow: 0 2px 8px -3px rgba(0,0,0,.35); transition: box-shadow .15s; }
        .ls-brand-btn:hover { box-shadow: 0 0 0 2px rgba(255,255,255,.45), 0 2px 8px -3px rgba(0,0,0,.35); }
        .ls-side-head .ls-wordmark { background: none; padding: 0; margin: 0; flex: 0 0 auto; overflow: visible; line-height: 0; }
        .ls-wordmark .cyt-brand { gap: 6px !important; }
        .ls-wordmark .cyt-brand > img { width: 26px; height: 26px; }
        .ls-wordmark .cyt-brand-small { font-size: 7.5px !important; }
        .ls-wordmark .cyt-brand-big { font-size: 15.5px !important; }
        .ls-brand-chev { color: #166534; font-size: 17px !important; opacity: 1; }
        .ls-side-head .ls-collapse { width: 34px; height: 34px; border-radius: 10px; color: #fff; background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.22); }
        .ls-side-head .ls-collapse:hover { background: rgba(255,255,255,.2); color: #fff; }
        .ls-side-head .ls-collapse svg { font-size: 19px !important; }
        @media (min-width: 1024px) {
          .ls-collapsed .ls-brand-btn { background: none; box-shadow: none; padding: 0; margin: 0; }
          .ls-collapsed .ls-side-head { padding: 0; }
        }
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .main-wrapper:not(.ls-drawer-open) .ls-brand-btn { background: none; box-shadow: none; padding: 0; margin: 0; }
          .main-wrapper:not(.ls-drawer-open) .ls-side-head { padding: 0; justify-content: center; }
        }


        /* ══ SIDEBAR v2 — one rhythm: every row (item or group) is 38px, same font; children indented ══ */
        .ls-nav { padding: 10px 10px 16px; }
        .ls-side .ls-item { font-size: 13.5px; font-weight: 600; color: #3f4a56; padding: 0 10px; height: 38px; border-radius: 10px; margin-bottom: 2px; gap: 11px; }
        .ls-side .ls-item-ic { width: 20px; height: 20px; }
        .ls-side .ls-item-ic svg { font-size: 19px !important; color: #6b7785; }
        .ls-side .ls-item:hover { background: #f1f7f3; color: #14532d; }
        .ls-side .ls-item:hover .ls-item-ic svg { color: #166534; }
        .ls-side .ls-item.on, .ls-collapsed .ls-side .ls-item.on, .main-wrapper .ls-side .ls-item.on { background: #e6f4ea; color: #14532d; font-weight: 700; box-shadow: none; }
        .ls-side .ls-item.on .ls-item-ic svg { color: #166534; }
        .ls-side .ls-item.on::before { content: ""; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 0 3px 3px 0; background: #c9962c; }

        .ls-sec { margin: 0; }
        .ls-gbtn { position: relative; width: 100%; height: 38px; display: flex; align-items: center; gap: 11px; padding: 0 8px 0 10px; margin-bottom: 2px; border: 0; border-radius: 10px; background: none; cursor: pointer; font-family: inherit; text-align: left; font-size: 13.5px; font-weight: 600; color: #3f4a56; white-space: nowrap; transition: background .12s, color .12s; }
        .ls-gbtn:hover { background: #f1f7f3; color: #14532d; }
        .ls-gbtn:hover .ls-item-ic svg { color: #166534; }
        .ls-gtitle { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
        .ls-sec.here > .ls-gbtn { color: #14532d; cursor: default; }
        .ls-sec.here > .ls-gbtn .ls-item-ic svg { color: #166534; }
        .ls-sec.here > .ls-gbtn:hover { background: none; }
        .ls-gcount { font-style: normal; font-size: 11px; font-weight: 700; min-width: 20px; height: 18px; padding: 0 6px; border-radius: 999px; background: #eef2f0; color: #6b7785; display: inline-flex; align-items: center; justify-content: center; }
        .ls-gchev { font-size: 18px !important; color: #9aa4ae; transition: transform .18s; transform: rotate(-90deg); flex-shrink: 0; opacity: 1; }
        .ls-sec.open .ls-gchev { transform: none; }
        .ls-sec.here .ls-gchev { visibility: hidden; }
        /* children: indented under a fine guide line, a touch smaller */
        .ls-sec .ls-sec-items { display: none; }
        .ls-sec.open .ls-sec-items { display: block; position: relative; margin: 0 0 6px 20px; padding-left: 8px; border-left: 1.5px solid #e3ebe6; animation: lsOpen .16s ease-out; }
        .ls-side .ls-sec-items .ls-item { height: 34px; font-size: 13px; font-weight: 500; gap: 9px; padding: 0 9px; }
        .ls-side .ls-sec-items .ls-item.on { font-weight: 700; }
        .ls-side .ls-sec-items .ls-item-ic svg { font-size: 17px !important; }
        .ls-side .ls-sec-items .ls-item.on::before { left: -9.5px; top: 7px; bottom: 7px; border-radius: 3px; }
        /* a little breathing room under the two top items */
        .ls-nav > a.ls-item:nth-of-type(2), .ls-nav > a.ls-item:only-of-type { margin-bottom: 8px; }

        /* icon rail (collapsed desktop / iPad without the drawer): every item, a hairline between groups */
        @media (min-width: 1024px) {
          .ls-collapsed .ls-side .ls-gbtn { display: none; }
          .ls-collapsed .ls-side .ls-sec { border-top: 1px solid #e3ebe6; margin: 6px 4px 0; padding-top: 6px; }
          .ls-collapsed .ls-side .ls-sec .ls-sec-items { display: block; margin: 0; padding: 0; border: 0; animation: none; }
          .ls-collapsed .ls-side .ls-item { justify-content: center; padding: 0; gap: 0; height: 38px; }
          .ls-collapsed .ls-side .ls-sec-items .ls-item.on::before { left: -4px; }
          .ls-collapsed .ls-side .ls-sec-items .ls-item-ic svg { font-size: 19px !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape) {
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-gbtn { display: none; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-sec { border-top: 1px solid #e3ebe6; margin: 6px 4px 0; padding-top: 6px; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-sec .ls-sec-items { display: block; margin: 0; padding: 0; border: 0; animation: none; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-item { justify-content: center; padding: 0; gap: 0; height: 38px; }
          .main-wrapper:not(.ls-drawer-open) .ls-side .ls-sec-items .ls-item.on::before { left: -4px; }
          .ls-drawer-open .ls-side .ls-sec-items .ls-item { justify-content: flex-start; gap: 9px; padding: 0 9px; }
          .ls-drawer-open .ls-side .ls-nav > a.ls-item { justify-content: flex-start; gap: 11px; padding: 0 10px; }
        }
        @media (max-width: 767px), (max-width: 1023px) and (orientation: portrait) { .ls-side { width: 272px; } }

        /* ══ SEARCH · NEW · PIN (top bar) ══ */
        .ls-search { display: flex; align-items: center; gap: 8px; height: 34px; width: min(360px, 34vw); margin-left: 14px; padding: 0 8px 0 10px; border: 1px solid var(--ls-line); border-radius: 10px; background: #f7faf8; color: var(--ls-muted); cursor: pointer; font-family: inherit; font-size: 12.5px; }
        .ls-search:hover { border-color: #bfe0cc; background: #fff; }
        .ls-search svg { font-size: 18px !important; color: var(--ls-accent); }
        .ls-search span { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ls-search kbd { font-family: inherit; font-size: 10.5px; font-weight: 700; color: #64748b; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 1px 6px; }
        .ls-pin { width: 30px; height: 30px; border: 0; background: none; border-radius: 8px; color: var(--ls-muted); cursor: pointer; display: grid; place-items: center; flex-shrink: 0; }
        .ls-pin:hover { background: var(--ls-hover); color: #c9962c; } .ls-pin.on { color: #c9962c; }
        .ls-pin svg { font-size: 18px !important; }
        .ls-quick { position: relative; flex-shrink: 0; }
        .ls-quick-btn { display: inline-flex; align-items: center; gap: 4px; height: 34px; padding: 0 12px 0 8px; border: 0; border-radius: 10px; background: #1e7a4c; color: #fff; font-family: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; }
        .ls-quick-btn:hover { background: #14532d; } .ls-quick-btn svg { font-size: 18px !important; }
        .ls-quick-menu { position: absolute; right: 0; top: calc(100% + 6px); min-width: 210px; background: #fff; border: 1px solid var(--ls-line); border-radius: 12px; box-shadow: 0 16px 40px rgba(20,22,25,.14); padding: 6px; z-index: 1010; animation: lsPop .14s ease both; }
        .ls-quick-menu a { display: block; padding: 8px 10px; border-radius: 8px; font-size: 13px; font-weight: 600; color: var(--ls-ink) !important; text-decoration: none !important; }
        .ls-quick-menu a:hover { background: var(--ls-hover); }
        @media (max-width: 1023px) { .ls-search { width: 34px; padding: 0; justify-content: center; margin-left: 6px; } .ls-search span, .ls-search kbd { display: none; } .ls-quick-btn span { display: none; } .ls-quick-btn { padding: 0 8px; } }

        /* ══ PINNED + "MORE" in the sidebar ══ */
        .ls-cap { display: block; margin: 10px 10px 4px; font-size: 10.5px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #9aa4ae; }
        .ls-cap.more { margin-top: 14px; padding-top: 10px; border-top: 1px solid #eef2f0; }
        .ls-pins { margin-bottom: 8px; }
        .ls-pin-row { position: relative; }
        .ls-unpin { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 22px; height: 22px; border: 0; border-radius: 6px; background: none; color: #9aa4ae; display: none; place-items: center; cursor: pointer; }
        .ls-pin-row:hover .ls-unpin { display: grid; } .ls-unpin:hover { background: #fee2e2; color: #b91c1c; } .ls-unpin svg { font-size: 14px !important; }
        @media (min-width: 1024px) { .ls-collapsed .ls-cap, .ls-collapsed .ls-unpin { display: none !important; } }
        .ls-lm-badge.amber { background: #d97706; }

        /* ══ HUB TABS (the pages of this hub, above the page) ══ */
        .hub-tabs { display: flex; align-items: center; gap: 12px; margin: -4px 0 14px; padding: 6px; background: #fff; border: 1px solid var(--ls-line); border-radius: 14px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif; }
        .hub-tabs-t { display: inline-flex; align-items: center; gap: 6px; padding: 0 6px 0 4px; color: #14532d; flex-shrink: 0; }
        .hub-tabs-t svg { font-size: 18px !important; color: #1e7a4c; } .hub-tabs-t b { font-size: 13px; font-weight: 800; white-space: nowrap; }
        .hub-tabs-row { display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none; min-width: 0; }
        .hub-tabs-row::-webkit-scrollbar { display: none; }
        .hub-tabs-row a { flex-shrink: 0; padding: 7px 12px; border-radius: 9px; font-size: 13px; font-weight: 600; color: #4b525a !important; text-decoration: none !important; white-space: nowrap; }
        .hub-tabs-row a:hover { background: #f1f7f3; color: #14532d !important; }
        .hub-tabs-row a.on { background: #e6f4ea; color: #14532d !important; font-weight: 800; }
        @media (max-width: 767px) { .hub-tabs { margin: -2px 0 12px; } .hub-tabs-t b { display: none; } }
        .mob-nav-item { position: relative; }
        .mob-badge { position: absolute; top: 4px; left: calc(50% + 6px); min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px; background: #d97706; color: #fff; font-style: normal; font-size: 10px; font-weight: 800; display: grid; place-items: center; }

        /* hide legacy admin-theme chrome */
        .top-nav-search,#toggle_btn,#mobile_btn,.mobile_btn,.header-left,.header { display:none !important; }
      `}</style>

      {/* ── LOCK SCREEN ── */}
      {isLocked && (
        <div className="lock-screen">
          <div className="lock-left">
            <div className="lock-watermark">CYT</div>
            <LockClock />
            <div className="lock-rule" />
            <div className="lock-brand">
              <div className="lock-brand-name">Choose Your<br />Therapist</div>
              <div className="lock-tagline">"Your journey to mental wellness starts with the right therapist."</div>
            </div>
            <div className="lock-admin-badge">Admin Portal</div>
          </div>
          <div className="lock-right">
            <div className="lock-unlock-box">
              <div className="lock-unlock-heading">
                <div className="lock-lock-icon"><i className="fe fe-lock" /></div>
                <div className="lock-unlock-title">Screen Locked</div>
                <div className="lock-unlock-sub">Enter your password to continue</div>
              </div>
              <div className="lock-input-wrap">
                <input
                  ref={inputRef}
                  type={showPass ? "text" : "password"}
                  className={`lock-input ${lockError ? "error" : ""} ${shake ? "shake" : ""}`}
                  placeholder="Enter password"
                  value={lockPass}
                  onChange={e => { setLockPass(e.target.value); setLockError(false); }}
                  onKeyDown={e => e.key === "Enter" && handleUnlock()}
                />
                <button className="lock-eye" onClick={() => setShowPass(p => !p)} tabIndex={-1}>
                  <i className={`fe fe-eye${showPass ? "-off" : ""}`} />
                </button>
              </div>
              {lockError && <div className="lock-error-msg">Incorrect password. Try again.</div>}
              <button className="lock-btn" onClick={handleUnlock}>Unlock →</button>
              <div className="lock-hint">Hint: password contains the platform name</div>
            </div>
          </div>
        </div>
      )}

      {/* ── SLIM TOP BAR ── */}
      <header className="ls-top">
        <button className="ls-burger" onClick={() => setDrawerOpen(o => !o)} aria-label="Menu">
          {drawerOpen ? <MenuOpenRounded /> : <MenuRounded />}
        </button>
        <span className="ls-page-title">{pageTitle}</span>
        {currentItem && !currentItem.alwaysVisible && (
          <button type="button" className={`ls-pin${pins.includes(currentItem.path) ? " on" : ""}`} onClick={() => togglePin(currentItem.path)}
            title={pins.includes(currentItem.path) ? "Unpin from the menu" : "Pin to the top of the menu"} aria-pressed={pins.includes(currentItem.path)}>
            {pins.includes(currentItem.path) ? <PushPinRounded /> : <StarBorderRounded />}
          </button>
        )}

        <button type="button" className="ls-search" onClick={() => setPaletteOpen(true)} aria-label="Search (Ctrl+K)">
          <SearchRounded /><span>Search clients, therapists, pages…</span><kbd>Ctrl K</kbd>
        </button>
        <span className="ls-top-sp" />
        {quickActions.length > 0 && (
          <div className="ls-quick">
            <button type="button" className="ls-quick-btn" onClick={() => setQuickOpen((o) => !o)} aria-haspopup="menu" aria-expanded={quickOpen} title="Quick add">
              <AddRounded /><span>New</span>
            </button>
            {quickOpen && (
              <div className="ls-quick-menu" role="menu">
                {quickActions.map((a) => <Link key={a.to} to={a.to} role="menuitem" onClick={() => setQuickOpen(false)}>{a.label}</Link>)}
              </div>
            )}
          </div>
        )}
        <TopClock firstName={firstName} />

        <AdminBell canSendPage={canSee("notifications")} />
        <Link className="ls-top-me" to="/edit-profile" title="Edit profile">
          <span className="ls-top-me-av">{adminAvatar ? <img src={adminAvatar} alt="" /> : (adminName || "A").trim().charAt(0).toUpperCase()}</span>
          <span className="ls-top-me-txt"><b>{adminName}</b><span>{isSuperAdmin ? "Super Admin" : "Admin"}</span></span>
        </Link>
      </header>

      {/* ── SIDEBAR ── */}
      <aside className="ls-side" ref={sideRef}>
        <div className="ls-side-head ls-acct-wrap">
          {/* the logo opens the account menu (Edit profile, Lock screen, Log out) */}
          <button type="button" className="ls-brand-btn" onClick={e => { e.stopPropagation(); setAccountOpen(o => !o); }}
            aria-haspopup="menu" aria-expanded={accountOpen} title={`${adminName} · account`}>
            <img className="ls-mark" src="/cyt-mark.svg" alt="Choose Your Therapist" />
            <span className="ls-wordmark"><BrandLogo size="sm" /></span>
            <ExpandMoreRounded className="ls-brand-chev" />
          </button>
          {accountOpen && accountMenu()}
          <button className="ls-collapse" onClick={toggleCollapsed} aria-label="Collapse sidebar" title="Collapse">
            {collapsed ? <MenuRounded /> : <MenuOpenRounded />}
          </button>
        </div>

        <nav className="ls-nav">
          {sections.map((s, si) => {
            if (!s.title) {
              return (
                <React.Fragment key={si}>
                  {s.items.map(navItem)}
                  {pinnedItems.length > 0 && (
                    <div className="ls-pins">
                      <span className="ls-cap">Pinned</span>
                      {pinnedItems.map((i) => (
                        <div key={i.path} className="ls-pin-row">
                          {navItem(i)}
                          <button type="button" className="ls-unpin" onClick={() => togglePin(i.path)} aria-label={`Unpin ${i.label}`} title="Unpin"><CloseRounded /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            const here = s.items.some(isOn);
            const open = here || openGroups.includes(s.title);
            const firstMore = s.more && !sections.slice(0, si).some((x) => x.more);
            return (
              <React.Fragment key={si}>
              {firstMore && <span className="ls-cap more">More</span>}
              <div className={`ls-sec${open ? " open" : ""}${here ? " here" : ""}`}>
                <button type="button" className="ls-gbtn" onClick={() => !here && toggleGroup(s.title)} aria-expanded={open}>
                  <span className="ls-item-ic">{s.icon}</span>
                  <span className="ls-gtitle">{s.title}</span>
                  {!open && <em className="ls-gcount">{s.items.length}</em>}
                  <ExpandMoreRounded className="ls-gchev" />
                </button>
                <div className="ls-sec-items">{s.items.map(navItem)}</div>
              </div>
              </React.Fragment>
            );
          })}
        </nav>

      </aside>

      {drawerOpen && <div className="ls-scrim" onClick={() => setDrawerOpen(false)} />}

      <div className="page-wrapper" style={noPad ? { paddingLeft: 0, paddingRight: 0, paddingBottom: 0 } : {}}>
        {hub && hub.items.length > 1 && !noPad && (
          <nav className="hub-tabs" aria-label={`${hub.title} pages`}>
            <span className="hub-tabs-t">{hub.icon}<b>{hub.title}</b></span>
            <div className="hub-tabs-row">
              {hub.items.map((i) => (
                <Link key={i.path} to={i.path} className={isOn(i) ? "on" : ""} aria-current={isOn(i) ? "page" : undefined}>{i.label}</Link>
              ))}
            </div>
          </nav>
        )}
        {children}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={visibleItems} actions={quickActions} />

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav className="mob-nav">
        {[
          { path: "/home",       icon: <SpaceDashboardRounded/>,     label: "Home" },
          { path: "/todo",       icon: <TaskAltRounded/>,            label: "To-do",     badge: todoCount },
          { path: "/clients",    icon: <Diversity3Rounded/>,         label: "Clients",   permKey: "clients" },
          { path: "/therapists", icon: <PsychologyRounded/>,         label: "Therapists", permKey: "therapists" },
        ].filter(i => !i.permKey || isSuperAdmin || (permsReady && canOpenPage(i.path, i.permKey))).map(item => (
          <Link key={item.path} to={item.path} className={`mob-nav-item ${pathname === item.path || pathname.startsWith(`${item.path}/`) ? "active" : ""}`}>
            {item.icon}<span>{item.label}</span>
            {item.badge > 0 && <i className="mob-badge">{item.badge}</i>}
          </Link>
        ))}
        <button className={`mob-nav-item ${sheet ? "active" : ""}`} onClick={() => { setSheetPage(0); setSheet(true); }}>
          <MenuRounded /><span>More</span>
        </button>
      </nav>
      {sheet && (
        <div className="mob-sheet-bg" role="presentation" onClick={() => setSheet(false)}>
          <div className="mob-sheet" role="dialog" aria-label="Menu" onClick={(e) => e.stopPropagation()}>
            <div className="mob-grab" aria-hidden="true" />
            <div className="mob-sheet-h">
              <b>Menu</b>
              <button type="button" onClick={() => setSheet(false)} aria-label="Close menu">×</button>
            </div>
            <div className="mob-pages" onScroll={(e) => setSheetPage(Math.round(e.currentTarget.scrollLeft / Math.max(1, e.currentTarget.clientWidth)))}>
              {sheetPages.map((pg, i) => (
                <div className="mob-page" key={i}>
                  {pg.map((item) => (
                    <Link key={item.path} to={item.path} className={`mob-tile ${isOn(item) ? "on" : ""}`} onClick={() => setSheet(false)}>
                      <span className="mob-tile-ic">{item.icon}</span>
                      <span className="mob-tile-l">{item.label}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            {sheetPages.length > 1 && (
              <div className="mob-dots" aria-hidden="true">{sheetPages.map((_, i) => <i key={i} className={i === sheetPage ? "on" : ""} />)}</div>
            )}
            {sheetPages.length > 1 && <small className="mob-swipe">Swipe for more →</small>}
          </div>
        </div>
      )}

      {/* ── GLOBAL ALERT BANNERS ── */}
      {leadsAlert && pathname !== "/leads" && (
        <GlobalAlert type="lead" alert={leadsAlert} top={lm.unacked.length > 0 ? 92 : 18}
          onClose={() => setLeadsAlert(null)}
          onView={() => { navigate("/leads"); setLeadsAlert(null); }} />
      )}
      {lm.unacked.length > 0 && (
        <div role="alert" style={{ position: "fixed", top: 18, left: "50%", transform: "translateX(-50%)", zIndex: 99999, width: "calc(100% - 32px)", maxWidth: 520, animation: "glSlide .35s cubic-bezier(.34,1.56,.64,1)" }}>
          <div style={{ background: "linear-gradient(135deg,#991b1b,#b91c1c,#dc2626)", borderRadius: 16, padding: "13px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 32px rgba(0,0,0,.45),0 0 0 1px rgba(255,255,255,.12)" }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>⚡</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                {lm.unacked.length > 1 ? `${lm.unacked.length} last-minute requests waiting` : "Last-minute request waiting"}
              </div>
              <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.9)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {lm.unacked.slice(0, 3).map(r => `${r.name} · ${r.slot}`).join("  |  ")}{lm.unacked.length > 3 ? ` +${lm.unacked.length - 3} more` : ""} — accept within 10 min
              </div>
              {dtPerm === "default" && (
                <button onClick={async () => setDtPerm(await requestDesktopAlerts())} style={{ marginTop: 6, background: "none", border: "none", padding: 0, color: "#fff", fontSize: 11.5, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Turn on desktop alerts</button>
              )}
            </div>
            {pathname !== "/noida-appointments" && (
              <button onClick={() => { lm.acknowledge(); navigate("/noida-appointments?view=lastMinute"); }} style={{ flexShrink: 0, background: "rgba(255,255,255,.22)", border: "1.5px solid rgba(255,255,255,.3)", borderRadius: 9, color: "#fff", fontSize: 12, fontWeight: 800, padding: "7px 14px", cursor: "pointer" }}>Review</button>
            )}
            <button onClick={lm.acknowledge} aria-label="Dismiss until the next request" title="Stop reminders for these" style={{ flexShrink: 0, background: "none", border: "none", color: "rgba(255,255,255,.75)", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>✕</button>
          </div>
        </div>
      )}
      {therapistAlert && canSee("therapists") && (
        <TherapistBar key={therapistAlert.at} alert={therapistAlert}
          onClose={() => setTherapistAlert(null)}
          onView={() => { navigate(therapistAlert.id ? `/therapists/${therapistAlert.id}` : "/therapists"); setTherapistAlert(null); }} />
      )}
    </div>
  );
}
